import { useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import {
  AppBar, Avatar, Box, Button, Container, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar,
  Typography,
} from "@mui/material";
import type { Breakpoint } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import PeopleIcon from "@mui/icons-material/People";
import InsightsIcon from "@mui/icons-material/Insights";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import PaidIcon from "@mui/icons-material/Paid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { NotificationsTray } from "@/components/NotificationsTray";

// The AP training hub (published on learn.gofreedompower.com). Opens in a new
// tab so reviewers can follow the guide alongside the app.
const GUIDE_URL =
  "https://learn.gofreedompower.com/AP-Creating-a-NetSuite-Bill-from-an-Invoice-End-to-End-3c9d90d9e24c81588af4f94167ad9f28";

// The bar keeps to one line: the daily pages sit in it, everything else
// (NetSuite Map, the guide, admin pages) is under More, and the account is a
// menu on the avatar. Icons show only where there's room for them.
export function AppLayout({ children, maxWidth = "lg" }: { children: ReactNode; maxWidth?: Breakpoint | false }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);

  const isActive = (to: string) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  const linkSx = (active: boolean) => ({
    textTransform: "none",
    fontWeight: active ? 700 : 400,
    whiteSpace: "nowrap",
    flexShrink: 0,
    minWidth: 0,
    borderRadius: 0,
    px: 1.25,
    alignSelf: "stretch",
    borderBottom: 2,
    borderColor: active ? "common.white" : "transparent",
    opacity: active ? 1 : 0.85,
    "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.08)" },
    "& .MuiButton-startIcon": { display: { xs: "none", lg: "inherit" } },
  });

  const navLink = (to: string, label: string, icon: ReactNode) => (
    <Button key={to} component={RouterLink} to={to} color="inherit" startIcon={icon} sx={linkSx(isActive(to))}>
      {label}
    </Button>
  );

  const moreItems: { to: string; label: string; icon: ReactNode; external?: boolean }[] = [
    { to: "/netsuite-map", label: "NetSuite Map", icon: <AccountTreeIcon fontSize="small" /> },
    { to: GUIDE_URL, label: "AP guide", icon: <MenuBookIcon fontSize="small" />, external: true },
    ...(user?.super_admin
      ? [
        { to: "/settings/users", label: "Users", icon: <PeopleIcon fontSize="small" /> },
        { to: "/settings/activity", label: "Activity", icon: <InsightsIcon fontSize="small" /> },
      ]
      : []),
  ];
  const moreActive = moreItems.some((i) => !i.external && isActive(i.to));
  const openMenu = (set: (el: HTMLElement | null) => void) => (e: MouseEvent<HTMLElement>) => set(e.currentTarget);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="primary">
        <Toolbar sx={{ gap: 1 }}>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Go to dashboard"
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: 1, flexShrink: 0, color: "inherit", textDecoration: "none" }}
          >
            <Box component="img" src="/logo-white.png" alt="Freedom Power" sx={{ height: 26 }} />
            <Typography variant="h6" sx={{ display: { xs: "none", xl: "block" }, whiteSpace: "nowrap" }}>FP Accounting</Typography>
          </Box>
          {user && (
            <Box component="nav" sx={{ display: "flex", alignSelf: "stretch", flexGrow: 1, minWidth: 0, overflowX: "auto", scrollbarWidth: "none" }}>
              {navLink("/", "AP Inbox", <InboxIcon fontSize="small" />)}
              {navLink("/settings/vendors", "Vendor Setup", <StorefrontIcon fontSize="small" />)}
              {navLink("/lightreach", "Lightreach AR", <SolarPowerIcon fontSize="small" />)}
              {navLink("/tesla", "Tesla RMA AR", <BatteryChargingFullIcon fontSize="small" />)}
              {navLink("/payment-sync", "Payments to Sunrise", <PaidIcon fontSize="small" />)}
              {navLink("/collections", "Collections", <ReceiptLongIcon fontSize="small" />)}
              {user.can_close_review && navLink("/close-review", "Close Review", <FactCheckIcon fontSize="small" />)}
              <Button color="inherit" endIcon={<ArrowDropDownIcon />} aria-haspopup="menu" onClick={openMenu(setMoreAnchor)}
                sx={linkSx(moreActive)}>
                More
              </Button>
              <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                {moreItems.flatMap((i, idx) => [
                  ...(idx === 2 ? [<Divider key="divider" />] : []),
                  <MenuItem
                    key={i.to}
                    selected={!i.external && isActive(i.to)}
                    onClick={() => setMoreAnchor(null)}
                    {...(i.external
                      ? { component: "a", href: i.to, target: "_blank", rel: "noopener noreferrer" }
                      : { component: RouterLink, to: i.to })}
                  >
                    <ListItemIcon>{i.icon}</ListItemIcon>
                    <ListItemText>{i.label}</ListItemText>
                    {i.external && <OpenInNewIcon sx={{ fontSize: 14, ml: 1, opacity: 0.6 }} />}
                  </MenuItem>,
                ])}
              </Menu>
            </Box>
          )}
          {user && (
            <>
              <NotificationsTray />
              <IconButton onClick={openMenu(setAccountAnchor)} aria-label={`Account: ${user.email}`} sx={{ p: 0.5, flexShrink: 0 }}>
                <Avatar src={user.avatar_url ?? undefined} alt={user.email} sx={{ width: 32, height: 32 }}>
                  {user.email[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <Box sx={{ px: 2, py: 1 }}>
                  {user.name && <Typography variant="subtitle2">{user.name}</Typography>}
                  <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                </Box>
                <Divider />
                <MenuItem component={RouterLink} to="/profile" onClick={() => setAccountAnchor(null)}>
                  <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Profile &amp; signatures</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setAccountAnchor(null); logout(); }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Sign out</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth={maxWidth} sx={{ py: 3 }}>
        {children}
      </Container>
    </Box>
  );
}
